import React, { useRef, useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import CallTransferDialogWrapper from 'flow/components/call-transfer-dialog/CallTransferDialogWrapper';
import ContactContextMenu from 'flow/components/context-menus/ContactContextMenu';
import BaseListItem, {
    rowMarking,
} from 'flow/components/ui-components/BaseListItem';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { selectExtensionActiveProfile } from 'flow/selectors/ExtensionSelectors';
import {
    ContactListContact,
    ContactListContactType,
    FuzzyMatch,
    selectContactListContact,
} from 'flow/selectors/contact-list-selectors';
import {
    ContactKey,
    ExtensionKey,
    ThirdPartyContactKey,
} from 'flow/types/EntityKeys';

import {
    extractName,
    extractNumberOrEmail,
    extractStatusFromProfile,
    isExtensionStatusInCall,
} from 'flow/utils';
import { isMobileDevice } from 'flow/utils/app-utils';
import { extensionToCallIsInCallWithYou } from 'flow/utils/call-utils-TW';
import {
    getFixedNumberFromContact,
    getFixedOrTechnicalOrMobileNumberFromExtension,
    getMobileNumberFromContact,
    getPhoneNumberFromContact,
} from 'flow/utils/extension-utils';
import { getParsedFuzzyTexts } from 'flow/utils/search-utils';
import { getIcon } from 'flow/utils/social-integration-utils';
import { twMerge } from 'tailwind-merge';
import CallAdaptiveButton from './CallAdaptiveButton';
import ContextMenu, { LockScroll } from './ContextMenu';
import NameField from './NameField';
import useTransferShortcuts from 'flow/hooks/useTransferShortcuts';
import { getMoreOptionsLabel } from 'flow/utils/a11y-utils';

interface Props {
    entityKey: ExtensionKey | ContactKey | ThirdPartyContactKey;
    fuzzyMatches?: FuzzyMatch[];
    showActionButtons: boolean;
    /** The transfer target: the transfer shortcuts act on this contact. */
    selected: boolean;
    /**
     * Whether this contact's page is open, in a list that marks the open page
     * apart from the transfer target. Without it, the target is grey.
     */
    isOpen?: boolean;
    onClick: (contact: ContactListContact, status: string) => void;
    contactType: ContactListContactType;
    lazyLoad?: boolean;
    disableIfNoNumber?: boolean;
}

// Read when the menu opens. The row can first render before the list's
// elements are on the page, and it does not render again with the list.
const lockScroll: LockScroll = {
    get scroll() {
        return document.querySelector('.left-nav-scroll');
    },
    get wrapper() {
        return document.querySelector('.left-nav-scroll-wrapper');
    },
};

const ExtensionListItem = ({
    entityKey,
    fuzzyMatches,
    showActionButtons,
    selected,
    isOpen,
    onClick,
    contactType,
    lazyLoad = true,
    disableIfNoNumber = false,
}: Props) => {
    const { i18next } = useAppContext();

    const { extension, status } = useExtension(entityKey as ExtensionKey);
    // BaseListItem takes no ref, so the shortcuts find the row through this
    // element in it.
    const rowContentRef = useRef<HTMLDivElement>(null);
    const { transferDialogIsShown, closeTransferDialog } = useTransferShortcuts(
        entityKey,
        selected,
        rowContentRef,
    );

    const [contextMenuIsActive, setContextMenuIsActive] = useState(false);

    const contact = useAppSelector((state) =>
        selectContactListContact(state, entityKey, contactType),
    );

    const showCallAdaptiveButton = useAppSelector(
        (state) => !extensionToCallIsInCallWithYou(state, extension),
    );

    let number: string =
        getFixedOrTechnicalOrMobileNumberFromExtension(extension);
    if (contactType === 'socialIntegrationContacts') {
        number = getPhoneNumberFromContact(contact);
    } else if (contactType === 'externalContacts') {
        const fixedNumber = getFixedNumberFromContact(contact);
        const mobileNumber = getMobileNumberFromContact(contact);
        number = fixedNumber || mobileNumber;
    }

    const [fuzzyParsedName, fuzzyParsedOther] = getParsedFuzzyTexts(
        fuzzyMatches,
        i18next,
        'search.contact',
    );

    const activeProfile = useAppSelector((state) =>
        selectExtensionActiveProfile(state, entityKey as ExtensionKey),
    );

    const statusText = extractStatusFromProfile(activeProfile, i18next);

    const disabled = disableIfNoNumber && !number;

    const initialItemDescription =
        fuzzyParsedOther ||
        (contactType === 'socialIntegrationContacts'
            ? extractNumberOrEmail(contact)
            : statusText || contact?.get('companyName'));

    const itemFlowStatus =
        typeof initialItemDescription === 'string' &&
        initialItemDescription.trim() === ''
            ? null
            : initialItemDescription;

    let icon = null;
    if (
        contactType === 'externalContacts' &&
        contact?.get('type') !== 'personal'
    ) {
        icon = <Icon iconKey="shared" size="1rem" />;
    }
    if (contactType === 'socialIntegrationContacts') {
        icon = getIcon(contact?.get('contactSource'), 16);
    }

    return (
        <>
            <BaseListItem
                marking={rowMarking({
                    pageOpen: isOpen,
                    highlighted:
                        contextMenuIsActive ||
                        (isOpen === undefined && selected),
                })}
                onClick={() => !disabled && onClick(contact, status)}
                className="badge-bg"
            >
                <ContactAvatar
                    contact={contact}
                    status={status}
                    lazyLoad={lazyLoad}
                    contactType={extension?.get('extensionType')}
                />
                <div
                    ref={rowContentRef}
                    className="flex flex-1 flex-col overflow-hidden whitespace-nowrap"
                    id={number}
                >
                    <NameField
                        contact={contact}
                        fuzzyParsedText={fuzzyParsedName}
                        icon={icon}
                        contextMenuIsActive={contextMenuIsActive}
                    />
                    {itemFlowStatus && (
                        <div className="truncate text-sm text-gray-500">
                            {itemFlowStatus}
                        </div>
                    )}
                </div>
                {showActionButtons && (
                    <div
                        className={twMerge(
                            'items-center justify-center gap-2',
                            isMobileDevice() || contextMenuIsActive
                                ? 'flex'
                                : 'sr-only-until-hover-or-focus flex',
                        )}
                    >
                        {contactType === 'extension' ||
                        contactType === 'externalContacts' ? (
                            <div className="flex items-center">
                                <ContextMenu
                                    triggerLabel={getMoreOptionsLabel(
                                        i18next,
                                        extractName(contact),
                                    )}
                                    preventOverflow={true}
                                    setActive={setContextMenuIsActive}
                                    targetKey={entityKey}
                                    lockScroll={lockScroll}
                                >
                                    <ContactContextMenu
                                        entityKey={entityKey as ExtensionKey}
                                    />
                                </ContextMenu>
                            </div>
                        ) : null}

                        {number && showCallAdaptiveButton ? (
                            <div className="flex items-center">
                                <CallAdaptiveButton
                                    extensionKey={extension?.get('key')}
                                    phoneNumber={number}
                                    extensionToCall={extension}
                                    activeBg={true}
                                />
                            </div>
                        ) : null}
                    </div>
                )}
            </BaseListItem>
            {transferDialogIsShown ? (
                <CallTransferDialogWrapper
                    phoneNumber={number}
                    contact={contact}
                    transferContact={contact}
                    handleClose={closeTransferDialog}
                    thisExtensionIsUnavailable={isExtensionStatusInCall(status)}
                />
            ) : null}
        </>
    );
};

export default ExtensionListItem;
